import { Options, Vue } from 'vue-class-component';
import { AdyenCheckout, Dropin } from '@adyen/adyen-web/auto';
import '@adyen/adyen-web/styles/adyen.css';
import { PaymentForModel, LIST_UPDATE_PA, AdyenAddress } from '../../../../../common/src';
import PaymentService from '@/services/payment.service';
import { Inject, Watch } from 'vue-property-decorator';
import { AdyenSessionDbModel, ErrorModel } from '../../../../../common/src';
import VueScrollTo from 'vue-scrollto';
import type { CoreConfiguration, DropinConfiguration } from '@adyen/adyen-web/auto';
import store from '@/store';
@Options({})
export default class AdyenDropIn extends Vue {
  @Inject('paymentService')
  public paymentService!: PaymentService;

  sessionId: string | null = null;
  clientKey: string | null = null;
  paymentKey: string | null = null;
  sData: string | null = null;
  redirectResult: string | null = null;
  environment: string | null = null;
  ccOnly: string | null = null;
  eftOnly: string | null = null;
  isPayementAgreement: string | null = null;
  operation: PaymentForModel | string | null = null;
  centerId: string | null = null;
  productId: string | null = null;
  sessionState: AdyenSessionDbModel | null = null;
  enableBillingAddress: boolean | null = false;
  billingAddress: AdyenAddress | null = null;
  adyenError: string | null = null;
  exerpError: string | null = null;
  checkingPayment: boolean | null = null;
  hideAdyenContainer = false;
  alreadyChecking: string | null = null;
  tmpCounter = 0;

  timeOut: number | undefined = undefined;
  // Tracks the single scheduled navigation to the result page, so failure routing stays idempotent
  // (in v6 the SDK can invoke onError and then onPaymentFailed for the same failure).
  resultRedirectTimeout: number | undefined = undefined;

  dropInContainer: Dropin | null = null;

  isMounted = false;
  isLoaded = false;

  get showStillChecking1(): boolean {
    return this.tmpCounter > 10 && this.tmpCounter <= 20;
  }

  get showStillChecking2(): boolean {
    return this.tmpCounter > 20;
  }

  mounted() {
    this.tmpCounter = 0;
    this.isMounted = true;
    this.mountDropIn();
  }

  beforeUnmount() {
    this.destroyTheComponent();
  }

  destroyTheComponent() {
    this.unmountDropin();
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    if (this.resultRedirectTimeout) {
      clearTimeout(this.resultRedirectTimeout);
      this.resultRedirectTimeout = undefined;
    }
  }

  @Watch('$store.state.currentLocale', { immediate: true, deep: true })
  localeChanged() {
    if (!this.isMounted) {
      return;
    }
    this.mountDropIn();
  }

  mountDropIn() {
    this.adyenError = null;
    this.exerpError = null;
    this.checkingPayment = null;

    // get url parameters
    const urlParams = this.$route.query;
    this.sessionId = urlParams.sessionId as string;
    this.clientKey = urlParams.clientKey as string;
    this.sData = urlParams.sData as string;
    this.redirectResult = urlParams.redirectResult as string;
    this.paymentKey = urlParams.paymentKey as string;
    this.environment = urlParams.environment as string;
    this.ccOnly = urlParams.ccOnly as string;
    this.eftOnly = urlParams.eftOnly as string;
    this.isPayementAgreement = urlParams.isPayementAgreement as string;
    this.operation = urlParams.payingFor as string as PaymentForModel;
    this.centerId = urlParams.centerId as string;
    this.productId = urlParams.productId as string;
    this.enableBillingAddress = urlParams.billingAddress === 'true';
    setTimeout(() => {
      VueScrollTo.scrollTo('body', 0, {});
    });

    // clear the timeout and remove the component
    this.destroyTheComponent();

    let allowPaymentMethods: string[] | undefined = undefined;
    if (this.eftOnly === '1') {
      allowPaymentMethods = ['paybybank_AIS_DD'];
    } else if (this.ccOnly === '1') {
      allowPaymentMethods = ['scheme', 'bcmc', 'bcmc_mobile', 'benefit', 'knet', 'applepay', 'googlepay', 'paypal', 'twint'];
    }

    const configuration: CoreConfiguration = {
      environment: this.environment as CoreConfiguration['environment'], // test or live
      clientKey: this.clientKey ?? undefined,
      allowPaymentMethods,
      analytics: {
        enabled: false, // Set to false to not send analytics data to Adyen.
      },
      session: {
        id: this.sessionId!, // Unique identifier for the payment session. countryCode is supplied by the /sessions setup response.
        sessionData: this.sData ?? undefined,
      },
      locale: store.state.currentLocale!.locale,
      onChange: (state: any, _component: any) => {
        this.billingAddress = state.data.billingAddress || null;
      },
      onPaymentCompleted: (result: any, _component: any) => {
        if (result.resultCode === 'Authorised') {
          this.hideAdyenContainer = true;
          this.checkingPayment = true;
          // replace the current route with the same query parameters but with the alreadyChecking set to true
          // in case some impatient member clicks refresh button, we don't show them the same payment page again with the same session
          this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, alreadyChecking: 'true' } });
          this.paymentService
            .finalizeAdyenSession(this.paymentKey!, result.sessionResult, this.billingAddress)
            .then(response => {
              this.handleSessionUpdate(response);
            })
            .catch((error: ErrorModel) => {
              this.exerpError = error.errorI18NMessage;
              this.onError(error.errorCode);
            })
            .finally(() => {
              this.hideAdyenContainer = true;
            });
        } else {
          this.hideAdyenContainer = true;
          this.onError(result.resultCode);
        }
      },
      onPaymentFailed: (result: any, _component: any) => {
        // In Adyen Web v6 this fires for Cancelled/Error/Refused result codes, but also after a
        // Sessions network/API failure — where the SDK has already called onError with the real
        // error and the payload here is an Error with no resultCode. Prefer the result code, fall
        // back to the raw payload (so an Error is serialized, not lost as undefined). onError is
        // idempotent, so when it already recorded the failure this is a no-op.
        this.hideAdyenContainer = true;
        this.onError(result?.resultCode ?? result ?? 'PAYMENT_FAILED');
      },
      onError: (error: any, _component: any) => {
        this.onError(error);
      },
    };

    if (urlParams.alreadyChecking) {
      this.checkPayment();
    } else {
      const checkoutPromise = AdyenCheckout(configuration);
      checkoutPromise
        .then(checkout => {
          this.isLoaded = true;
          if (!this.adyenError) {
            if (!this.redirectResult) {
              // In Adyen Web v6 paymentMethodsConfiguration moves off the checkout config onto
              // the Drop-in config, and the Drop-in is created via constructor instead of create().
              // Any payment method specific configuration. Find the configuration specific to each payment method: https://docs.adyen.com/payment-methods
              // For example, this is 3D Secure configuration for cards:
              const dropinConfiguration: DropinConfiguration = {
                showStoredPaymentMethods: false,
                paymentMethodsConfiguration: {
                  card: {
                    hasHolderName: true,
                    holderNameRequired: true,
                    billingAddressRequired: this.enableBillingAddress ?? false,
                  },
                },
              };
              if (this.$t('payment.adyenPayementCreditCardNameOverride')) {
                (dropinConfiguration.paymentMethodsConfiguration as any).card.name = this.$t('payment.adyenPayementCreditCardNameOverride');
              }
              this.dropInContainer = new Dropin(checkout, dropinConfiguration).mount('#dropin-container');
            } else {
              checkout.submitDetails({ details: { redirectResult: this.redirectResult } });
            }
          }
        })
        .catch((error: any) => {
          // In Adyen Web v6 a failed /sessions/{id}/setup rejects this promise in addition to
          // invoking configuration.onError, which already records the error and routes to the
          // failure result page. Only handle here when onError has not already done so, so we
          // never route to the failure result twice.
          if (this.adyenError) {
            console.error('Adyen checkout initialization failed', error);
          } else {
            this.onError(error);
          }
        });
    }
  }

  private unmountDropin() {
    // empty the div with id dropin-container
    if (this.dropInContainer) {
      this.dropInContainer.unmount();
      this.dropInContainer = null;
    }
    const dropinContainer = document.getElementById('dropin-container');
    if (dropinContainer) {
      dropinContainer.innerHTML = '';
    }
    this.isLoaded = false;
  }

  checkPayment() {
    this.checkingPayment = true;
    this.tmpCounter++;
    this.paymentService
      .checkAdyenPayment(this.paymentKey!)
      .then(result => {
        this.handleSessionUpdate(result);
      })
      .catch((e: ErrorModel) => {
        this.exerpError = e.errorI18NMessage;
        this.onError(e.errorCode);
      });
  }

  private handleSessionUpdate(session: AdyenSessionDbModel | null): void {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
      this.timeOut = undefined;
    }

    if (!session) {
      this.onError('SESSION_NOT_FOUND');
      return;
    }

    this.sessionState = session;

    const terminalStatuses: AdyenSessionDbModel['status'][] = ['COMPLETED', 'FAILED', 'REFUND_IN_PROGRESS'];
    if (terminalStatuses.includes(session.status)) {
      this.redirectToResultPage(session.status);
      return;
    }

    if (this.tmpCounter >= 65) {
      this.onError('TIMEDOUT');
      return;
    }

    this.timeOut = window.setTimeout(() => this.checkPayment(), 6000);
  }

  private redirectToResultPage(status: AdyenSessionDbModel['status']): void {
    // Only ever schedule one navigation to the result page per mount.
    if (this.resultRedirectTimeout !== undefined) {
      return;
    }
    if (this.timeOut) {
      clearTimeout(this.timeOut);
      this.timeOut = undefined;
    }

    const isSuccess = status === 'COMPLETED';
    const isFailure = status === 'FAILED' || status === 'REFUND_IN_PROGRESS';
    const resultStatus = isSuccess ? 'success' : 'failure';

    this.dropInContainer?.unmount();

    const query: Record<string, any> = {
      status: resultStatus,
      paymentKey: this.paymentKey,
    };

    if (isSuccess) {
      query.sessionId = this.sessionId;
    }

    if (isFailure && this.adyenError) {
      query.error = this.adyenError;
    }

    query.operation = this.operation;
    query.centerId = this.centerId;
    query.productId = this.productId;

    this.resultRedirectTimeout = window.setTimeout(() => {
      this.checkingPayment = false;
      this.$router.replace({
        path: '/payment-result/adyen',
        query,
      });
    }, 4000);
  }

  /**
   * Maps Adyen billing address format to the expected internal address format
   * Only includes keys with valid data (non-empty and not "N/A")
   * @param adyenAddress - The billing address from Adyen's state.data.billingAddress
   * @returns Mapped address object or null if no address provided
   */
  onError(error: any) {
    // Idempotent — the first failure wins. In v6 the SDK can invoke onError and then
    // onPaymentFailed for the same network/API failure; once a diagnostic is recorded, keep it and
    // do not route again (redirectToResultPage is guarded too).
    if (this.adyenError) {
      return;
    }
    if (this.timeOut) {
      clearTimeout(this.timeOut);
      this.timeOut = undefined;
    }
    this.adyenError = safeStringify(error, 500) || 'PAYMENT_FAILED';
    this.redirectToResultPage('FAILED');
  }
}

function safeStringify(obj: any, limit: number): string {
  if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean' || !obj) {
    return obj;
  }
  if (obj.toString && obj.toString !== Object.prototype.toString) {
    return obj.toString();
  }
  let result;
  try {
    const seen = new WeakSet();
    result = JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return; // Remove cyclic references
        }
        seen.add(value);
      }
      return value;
    });
  } catch (e: any) {
    result = 'Error while parsing the error ' + e.message;
  }
  if (result.length > limit) {
    return result.substring(0, limit) + '...';
  }
  return result;
}
